import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import {
  Lock,
  Unlock,
  FileText,
  Download,
  ArrowUpRight,
  Video,
  ClipboardList,
  Map,
  Sparkles,
  CheckCircle2,
} from "lucide-react";
import { Link } from "react-router-dom";

const UNLOCK_KEY = "cs_insider_unlocked";

const INSIDER_RESOURCES = [
  {
    Icon: FileText,
    tag: "PDF · Free",
    title: "South Bay Homebuyer Cheat Sheet",
    desc: "Our full 2-page buying-power worksheet. Prints beautifully.",
    href: "/downloads/south-bay-homebuyer-cheat-sheet.pdf",
    label: "Download PDF",
  },
  {
    Icon: ClipboardList,
    tag: "Worksheet · Coming soon",
    title: "Seller's Net Sheet Worksheet",
    desc: "The same math we run to protect your walk-away number.",
    href: null,
    label: "Coming soon",
  },
  {
    Icon: Map,
    tag: "Guide · Coming soon",
    title: "South Bay Neighborhood Deep-Dive",
    desc: "The real story on Hermosa vs. MB vs. RPV, from people who actually live here.",
    href: null,
    label: "Coming soon",
  },
  {
    Icon: Video,
    tag: "Video · Coming soon",
    title: "Buy → Build Equity → Trade Up",
    desc: "A 12-minute walk-through of the exact wealth-building playbook.",
    href: null,
    label: "Coming soon",
  },
];

export default function InsiderResources() {
  const [unlocked, setUnlocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "" });

  useEffect(() => {
    setUnlocked(localStorage.getItem(UNLOCK_KEY) === "true");
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.city) {
      toast.error("All fields are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/resources/insider-access", form);
      if (data?.success) {
        localStorage.setItem(UNLOCK_KEY, "true");
        setUnlocked(true);
        toast.success(data.message || "You're in.");
        setTimeout(() => {
          const el = document.querySelector('[data-testid="insider-vault"]');
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 200);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      toast.error("Couldn't submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    localStorage.removeItem(UNLOCK_KEY);
    setUnlocked(false);
    setForm({ name: "", email: "", phone: "", city: "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div data-testid="insider-resources-page">
      {/* Hero */}
      <section
        className="section pt-20 pb-16"
        style={{ backgroundColor: "#1e4d5d" }}
        data-testid="insider-hero"
      >
        <div className="container-edge text-white">
          <p className="overline flex items-center gap-2" style={{ color: "#e8c79a" }}>
            <Sparkles size={14} /> Insider Resources · Free
          </p>
          <h1
            className="mt-6 text-5xl md:text-6xl lg:text-7xl max-w-4xl"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            The guides we <em className="not-italic" style={{ color: "#e8c79a", fontStyle: "italic" }}>only share</em> with insiders.
          </h1>
          <p className="mt-6 text-white/85 max-w-2xl text-lg font-light leading-relaxed">
            Cheat sheets, worksheets, and neighborhood deep-dives we've built for our clients —
            free to anyone who takes 30 seconds to introduce themselves. No spam. No sales calls
            you didn't ask for.
          </p>
        </div>
      </section>

      {/* Gate OR Vault */}
      {!unlocked ? (
        <section className="section" data-testid="insider-gate">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="overline flex items-center gap-2" style={{ color: "var(--secondary)" }}>
                <Lock size={14} /> Step 1 · Say hi
              </p>
              <h2
                className="mt-4 text-4xl md:text-5xl"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Tell us who you are — we'll unlock everything.
              </h2>
              <p className="mt-6 text-soft leading-relaxed">
                One quick form. Instant access. We'll shoot the resources over and won't call unless
                you tell us to.
              </p>
              <ul className="mt-8 space-y-3 text-sm text-soft">
                <li className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-sand mt-0.5 shrink-0" />
                  Free · No credit card, no strings
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-sand mt-0.5 shrink-0" />
                  Instant unlock — no email verification
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-sand mt-0.5 shrink-0" />
                  Personally reviewed by the Champagne &amp; Sunshine team
                </li>
              </ul>
            </div>

            <div className="lg:col-span-7">
              <form
                onSubmit={handleSubmit}
                className="bg-paper border border-soft p-8 md:p-12"
                data-testid="insider-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="overline block mb-2">Full name *</label>
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="Jane Smith"
                      className="w-full bg-transparent border-b border-soft py-3 text-lg focus:border-sand focus:outline-none transition-colors"
                      data-testid="insider-name"
                    />
                  </div>
                  <div>
                    <label className="overline block mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="jane@email.com"
                      className="w-full bg-transparent border-b border-soft py-3 text-lg focus:border-sand focus:outline-none transition-colors"
                      data-testid="insider-email"
                    />
                  </div>
                  <div>
                    <label className="overline block mb-2">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      placeholder="310.555.1234"
                      className="w-full bg-transparent border-b border-soft py-3 text-lg focus:border-sand focus:outline-none transition-colors"
                      data-testid="insider-phone"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="overline block mb-2">City *</label>
                    <input
                      name="city"
                      value={form.city}
                      onChange={handleChange}
                      required
                      placeholder="Manhattan Beach"
                      className="w-full bg-transparent border-b border-soft py-3 text-lg focus:border-sand focus:outline-none transition-colors"
                      data-testid="insider-city"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary mt-10 w-full md:w-auto inline-flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="insider-submit"
                >
                  {submitting ? (
                    "Unlocking…"
                  ) : (
                    <>
                      <Unlock size={16} className="mr-2" /> Unlock the resources
                    </>
                  )}
                </button>
                <p className="mt-6 text-xs text-soft">
                  We never sell your info. We may reach out once to say hi — that's it.
                </p>
              </form>
            </div>
          </div>
        </section>
      ) : (
        <>
          <section className="section" data-testid="insider-welcome">
            <div className="bg-paper border border-soft p-10 md:p-14">
              <div className="flex items-start gap-4">
                <CheckCircle2 size={24} className="text-sand mt-1 shrink-0" />
                <div>
                  <p className="overline" style={{ color: "var(--secondary)" }}>You're in</p>
                  <h2
                    className="mt-2 text-3xl md:text-4xl"
                    style={{ fontFamily: "Playfair Display, serif" }}
                  >
                    Welcome to the Insider vault.
                  </h2>
                  <p className="mt-4 text-soft leading-relaxed max-w-2xl">
                    Everything below is yours to keep. We'll be adding new pieces every month —
                    bookmark this page and check back.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="section pt-4" data-testid="insider-vault">
            <SectionHeader overline="Your unlocked vault" title="Take what's useful." />
            <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {INSIDER_RESOURCES.map(({ Icon, tag, title, desc, href, label }, i) => (
                <div
                  key={title}
                  className="editorial-card p-8 flex flex-col"
                  data-testid={`insider-resource-${i}`}
                >
                  <Icon size={26} className="text-sand" strokeWidth={1.25} />
                  <p className="overline mt-6">{tag}</p>
                  <h3
                    className="mt-3 text-xl"
                    style={{ fontFamily: "Playfair Display, serif" }}
                  >
                    {title}
                  </h3>
                  <p className="mt-3 text-soft text-sm flex-1">{desc}</p>
                  {href ? (
                    <a
                      href={href}
                      download
                      className="mt-6 inline-flex items-center text-sm text-ocean hover:text-sand transition-colors self-start"
                      data-testid={`insider-download-${i}`}
                    >
                      {label} <Download size={14} className="ml-2" />
                    </a>
                  ) : (
                    <span className="mt-6 inline-flex items-center text-sm text-soft self-start">
                      {label}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="section" data-testid="insider-cta">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-linen p-10 md:p-14">
              <div className="lg:col-span-8">
                <p className="overline">Ready to talk?</p>
                <h3
                  className="mt-3 text-3xl md:text-4xl"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Have a question about anything you just downloaded?
                </h3>
                <p className="mt-4 text-soft max-w-xl">
                  We're a phone call away. No pressure, no pitch — just answers.
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <Link
                  to="/contact"
                  className="btn-primary inline-flex"
                  data-testid="insider-contact-cta"
                >
                  Book a chat <ArrowUpRight size={14} className="ml-2" />
                </Link>
              </div>
            </div>
            <button
              onClick={handleReset}
              className="mt-8 text-xs text-soft underline hover:text-ocean transition-colors"
              data-testid="insider-reset"
            >
              Reset access (preview mode)
            </button>
          </section>
        </>
      )}
    </div>
  );
}
